// Port of ios/Sources/ViewModels/PastedTable.swift.
//
// A table copied out of Excel, Numbers, Sheets or a web page, turned into Markdown.
//
// Every one of those puts the same thing on the pasteboard's PLAIN-TEXT flavour: rows separated
// by newlines, cells by TABS. Pasted as-is that arrives as a wall of tab-separated lines which
// this app's renderer has no reason to read as a table — the columns collapse and the numbers
// stop lining up (user report, 2026-09-07). Rewriting it as a Markdown table is what makes the
// paste arrive as the thing that was copied: the message renderer already draws those as real
// grids, with their own copy affordance.
//
// Pure, so the shape recognition is testable without a pasteboard. (WEB: the caller feeds it
// `clipboardData.getData('text/plain')` from the composer's paste event.)

import { trimWhitespaces } from './swiftCharacters';

/**
 * Null when this is not a table — the caller then lets the ordinary text paste happen. Being
 * conservative matters more than being clever here: a false positive would rewrite somebody's
 * pasted log or code into a grid.
 */
function markdown(fromPlainText: string): string | null {
  const rows = parse(fromPlainText);
  if (rows.length < 2) return null;
  const width = rows.reduce((max, row) => Math.max(max, row.length), 0);
  if (width < 2) return null;

  const padded = rows.map((row) => [...row, ...new Array<string>(width - row.length).fill('')]);
  const lines: string[] = [];
  lines.push(render(padded[0]));
  lines.push('|' + ' --- |'.repeat(width));
  for (const row of padded.slice(1)) lines.push(render(row));
  return lines.join('\n');
}

/**
 * The rows, as cells, or an empty result when the text is not tab-separated in a
 * table-shaped way.
 */
function parse(text: string): string[][] {
  const lines = text
    .replaceAll('\r\n', '\n')
    .replaceAll('\r', '\n')
    .split('\n')
    .filter((line) => trimWhitespaces(line) !== '');
  if (lines.length < 2) return [];
  // EVERY line must carry a tab. One tabbed line among prose is an indented sentence, not a
  // table, and rewriting that would be worse than doing nothing.
  if (!lines.every((line) => line.includes('\t'))) return [];
  const rows = lines.map((line) => line.split('\t').map(trimWhitespaces));
  // Ragged output is the sign of something that merely contains tabs (a log, a code block);
  // a real table's rows agree on their width, give or take a trailing empty cell.
  const widths = new Set(rows.map((row) => row.length));
  const widest = Math.max(...widths);
  const narrowest = Math.min(...widths);
  if (!(widths.size <= 2 && widest - narrowest <= 1)) return [];
  return rows;
}

function render(row: readonly string[]): string {
  // A pipe inside a cell would end the cell; escaped, it survives the round trip.
  return '| ' + row.map((cell) => cell.replaceAll('|', '\\|')).join(' | ') + ' |';
}

export const PastedTable = {
  /** Swift `markdown(fromPlainText:)`. */
  markdown,
} as const;
